import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Sparkles, FileText, CalendarDays, CheckCircle2,
  AlertTriangle, Linkedin, ArrowRight, Settings2, Link2, Unlink,
  Heart, MessageCircle, Flame, Clock, ExternalLink,
} from "lucide-react";

const Stat = ({ icon: Icon, label, value, hint }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-6 transition-transform hover:-translate-y-[1px]">
    <div className="flex items-center gap-2 text-slate-400 mb-3">
      <Icon className="h-4 w-4" />
      <span className="text-xs font-semibold uppercase tracking-[0.15em]">{label}</span>
    </div>
    <p className="font-serif text-3xl text-slate-900">{value}</p>
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [usage, setUsage] = useState(null);
  const [posts, setPosts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [li, setLi] = useState(null);
  const [billing, setBilling] = useState(null);
  const [stats, setStats] = useState(null);

  const loadLi = () => api.get("/linkedin/status").then((r) => setLi(r.data));

  useEffect(() => {
    api.get("/usage").then((r) => setUsage(r.data));
    api.get("/analytics/overview").then((r) => setStats(r.data)).catch(() => {});
    api.get("/posts").then((r) => setPosts(r.data));
    api.get("/style-profile").then((r) => setProfile(r.data));
    api.get("/billing/config").then((r) => setBilling(r.data)).catch(() => {});
    loadLi();
    const st = searchParams.get("linkedin");
    if (st === "connected") toast.success("Compte LinkedIn connecté avec succès.");
    if (st === "error") toast.error("La connexion LinkedIn a échoué. Réessayez.");
    if (st) { searchParams.delete("linkedin"); setSearchParams(searchParams, { replace: true }); }
    if (searchParams.get("reconnect") === "linkedin") {
      searchParams.delete("reconnect"); setSearchParams(searchParams, { replace: true });
      connectLinkedIn();
    }
  }, []); // eslint-disable-line

  const connectLinkedIn = async () => {
    try {
      const { data } = await api.get("/linkedin/auth-url");
      window.location.href = data.url;
    } catch (e) {
      toast.error("LinkedIn n'est pas encore configuré (Client ID/Secret).");
    }
  };

  const disconnectLinkedIn = async () => {
    await api.post("/linkedin/disconnect");
    toast.success("Compte LinkedIn déconnecté.");
    loadLi();
  };

  const count = (s) => posts.filter((p) => p.status === s).length;
  const hasProfile = profile && profile.example_posts?.length > 0;
  const isAdmin = user?.role === "admin";

  const fmtDay = (iso) => (iso
    ? new Date(iso).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", day: "numeric", month: "short" })
    : "");
  const fmtDayTime = (iso) => (iso
    ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Europe/Paris", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })
    : "");

  const eng = stats?.engagement;
  const prev = stats?.published_prev_month ?? 0;
  const cur = stats?.published_this_month ?? 0;
  const monthTrend = prev === 0
    ? (cur > 0 ? "premier mois de publication" : "aucun post publié le mois dernier")
    : `${cur >= prev ? "+" : ""}${cur - prev} vs mois dernier (${prev})`;
  const nextAt = stats?.upcoming?.[0]?.at ? fmtDay(stats.upcoming[0].at) : null;

  return (
    <div className="p-8 lg:p-12">
      <div className="mb-2 flex items-center gap-2 text-coral-dark">
        <Sparkles className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em]">Tableau de bord</span>
      </div>
      <h1 className="font-serif text-4xl text-slate-900 mb-1">Bonjour, {user?.name?.split(" ")[0]}.</h1>
      <p className="text-slate-500 mb-10">Voici l'état de votre studio éditorial.</p>

      {billing?.billing_mode === "active" && billing?.entitlement && !billing.entitlement.active && (
        <div data-testid="readonly-banner" className="flex items-start justify-between gap-4 bg-slate-50 border-2 border-slate-300 rounded-lg p-5 mb-6">
          <div className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-sm text-slate-800">Accès en lecture seule</p>
              <p className="text-slate-600 text-sm mt-0.5">
                {billing.entitlement.expired ? "Votre essai est terminé." : "Aucune formule active."} <strong>Tous vos posts et données sont conservés.</strong> Choisissez une formule pour reprendre la création.
              </p>
            </div>
          </div>
          <button data-testid="readonly-cta" onClick={() => navigate("/tarifs")}
            className="shrink-0 flex items-center gap-1.5 text-sm font-bold bg-coral-strong text-white px-4 py-2.5 rounded-lg hover:bg-coral-hover transition-colors">
            Voir les formules <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {billing?.entitlement?.source === "invite" && billing.entitlement.active && billing.trial_ends_at && (
        <div data-testid="trial-banner" className="flex items-center justify-between gap-4 bg-coral-soft border border-coral/30 rounded-lg p-4 mb-6">
          <p className="text-sm text-coral-dark">
            <Sparkles className="h-4 w-4 inline mr-1.5 -mt-0.5" />
            Il vous reste <strong>{Math.max(0, Math.ceil((new Date(billing.trial_ends_at) - new Date()) / 86400000))} jour(s)</strong> pour profiter pleinement de Pulse.
          </p>
          <button data-testid="trial-cta" onClick={() => navigate("/tarifs")}
            className="shrink-0 text-sm font-bold text-coral-dark hover:underline whitespace-nowrap">Choisir ma formule →</button>
        </div>
      )}

      {/* LinkedIn connection card */}
      {li?.connected && li?.reauth_required ? (
        <div data-testid="linkedin-banner" className="flex items-start justify-between gap-4 bg-red-50 border-2 border-red-300 rounded-lg p-5 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-sm text-red-800">Reconnexion LinkedIn nécessaire</p>
              <p className="text-red-700 text-sm mt-0.5">
                Votre autorisation LinkedIn a expiré. Reconnectez votre compte pour reprendre les publications automatiques.
              </p>
            </div>
          </div>
          <button data-testid="linkedin-reconnect-btn" onClick={connectLinkedIn}
            className="shrink-0 flex items-center gap-1.5 text-sm font-bold bg-red-600 text-white px-4 py-2.5 rounded-lg hover:bg-red-700 transition-colors">
            <Link2 className="h-4 w-4" /> Reconnecter maintenant
          </button>
        </div>
      ) : li?.connected && li?.expiring_soon ? (
        <div data-testid="linkedin-banner" className="flex items-start justify-between gap-4 bg-amber-50 border-2 border-amber-300 rounded-lg p-5 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-sm text-amber-900">Votre connexion LinkedIn expire bientôt</p>
              <p className="text-amber-800 text-sm mt-0.5">
                Il reste <strong>{li.days_until_expiry} jour(s)</strong> avant l'expiration de votre autorisation.
                Reconnectez maintenant en un clic pour ne pas interrompre vos publications automatiques (repart pour 60 jours).
              </p>
            </div>
          </div>
          <button data-testid="linkedin-reconnect-btn" onClick={connectLinkedIn}
            className="shrink-0 flex items-center gap-1.5 text-sm font-bold bg-amber-500 text-white px-4 py-2.5 rounded-lg hover:bg-amber-600 transition-colors">
            <Link2 className="h-4 w-4" /> Reconnecter maintenant
          </button>
        </div>
      ) : (
      <div data-testid="linkedin-banner" className="flex items-start justify-between gap-4 bg-white border border-border rounded-lg p-5 mb-8">
        <div className="flex items-start gap-3">
          <Linkedin className="h-5 w-5 text-coral shrink-0 mt-0.5" />
          <div>
            {li?.connected ? (
              <>
                <p className="font-bold text-sm text-ink">LinkedIn connecté, {li.name}</p>
                <p className="text-ink-light text-sm mt-0.5">Publication réelle active sur votre profil personnel.</p>
              </>
            ) : li?.configured ? (
              <>
                <p className="font-bold text-sm text-ink">Connectez votre compte LinkedIn</p>
                <p className="text-ink-light text-sm mt-0.5">
                  Autorisez Pulse à publier sur votre profil (scope w_member_social).
                </p>
              </>
            ) : (
              <>
                <p className="font-bold text-sm text-ink">Connexion LinkedIn, mode simulation</p>
                <p className="text-ink-light text-sm mt-0.5">
                  La publication est simulée. Configurez votre app LinkedIn pour activer la publication réelle
                  (OAuth, tokens 60 jours), sans changement d'interface.
                </p>
              </>
            )}
          </div>
        </div>
        {li?.configured && (
          li.connected ? (
            <button data-testid="linkedin-disconnect-btn" onClick={disconnectLinkedIn}
              className="shrink-0 flex items-center gap-1.5 text-sm font-semibold border border-border text-ink-light px-3.5 py-2 rounded-lg hover:bg-secondary transition-colors">
              <Unlink className="h-4 w-4" /> Déconnecter
            </button>
          ) : (
            <button data-testid="linkedin-connect-btn" onClick={connectLinkedIn}
              className="shrink-0 flex items-center gap-1.5 text-sm font-semibold bg-coral-strong text-white px-3.5 py-2 rounded-lg hover:bg-coral-hover transition-colors">
              <Link2 className="h-4 w-4" /> Connecter LinkedIn
            </button>
          )
        )}
      </div>
      )}

      {/* Profile prompt */}
      {!hasProfile && (
        <div className="flex items-center justify-between bg-coral-soft border border-coral/40 rounded-sm p-5 mb-8">
          <div className="flex items-center gap-3">
            <Settings2 className="h-5 w-5 text-coral-dark" />
            <p className="text-sm text-slate-700 font-medium">
              Commencez par définir votre profil de style pour des posts calibrés sur votre voix.
            </p>
          </div>
          <button data-testid="goto-onboarding-btn" onClick={() => navigate("/onboarding")}
            className="flex items-center gap-1.5 text-sm font-semibold text-coral-dark hover:underline shrink-0">
            Définir mon profil <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat icon={CheckCircle2} label="Publiés ce mois-ci" value={stats?.published_this_month ?? count("published")}
          hint={stats ? monthTrend : `${count("published")} au total`} />
        <Stat icon={Flame} label="Régularité" value={`${stats?.weeks_streak ?? 0} sem.`}
          hint={(stats?.weeks_streak ?? 0) > 0 ? "semaines d'affilée avec un post" : "publiez cette semaine pour lancer la série"} />
        <Stat icon={FileText} label="Brouillons" value={count("draft")} hint="prêts à programmer" />
        <Stat icon={CalendarDays} label="Programmés" value={count("scheduled")}
          hint={nextAt ? `prochain le ${nextAt}` : "aucun pour l'instant"} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h3 className="font-serif text-2xl text-slate-900 mb-4">Actions rapides</h3>
          <div className="space-y-3">
            <button data-testid="quick-generate-btn" onClick={() => navigate("/creer")}
              className="w-full flex items-center justify-between bg-coral-strong text-white px-5 py-4 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors">
              <span className="flex items-center gap-2"><Sparkles className="h-4 w-4" /> Créer un nouveau post</span>
              <ArrowRight className="h-4 w-4" />
            </button>
            <button data-testid="quick-calendar-btn" onClick={() => navigate("/calendrier")}
              className="w-full flex items-center justify-between border border-slate-200 px-5 py-4 rounded-sm text-sm font-semibold text-slate-700 hover:border-coral transition-colors">
              <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4" /> Voir le calendrier éditorial</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Performance des posts publiés sur LinkedIn */}
        <div data-testid="performance-card" className="bg-white border border-slate-200 rounded-sm p-6">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-serif text-2xl text-slate-900">Vos posts sur LinkedIn</h3>
            {eng?.available && (
              <span className="text-xs text-slate-400">{eng.posts_measured} dernier(s) post(s)</span>
            )}
          </div>

          {eng?.available ? (
            <>
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div className="bg-coral-soft/50 rounded-sm p-4">
                  <div className="flex items-center gap-2 text-coral-dark mb-1">
                    <Heart className="h-4 w-4" />
                    <span className="text-xs font-semibold uppercase tracking-[0.12em]">Réactions</span>
                  </div>
                  <p className="font-serif text-3xl text-slate-900">{eng.reactions}</p>
                </div>
                <div className="bg-slate-50 rounded-sm p-4">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <MessageCircle className="h-4 w-4" />
                    <span className="text-xs font-semibold uppercase tracking-[0.12em]">Commentaires</span>
                  </div>
                  <p className="font-serif text-3xl text-slate-900">{eng.comments}</p>
                </div>
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400 mb-2">Vos posts les plus engageants</p>
              <div className="space-y-2">
                {eng.top.map((t) => (
                  <a key={t.id} href={t.url} target="_blank" rel="noreferrer" data-testid={`top-post-${t.id}`}
                    className="flex items-start gap-3 p-3 border border-slate-100 rounded-sm hover:border-coral transition-colors group">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700 truncate">{t.excerpt}…</p>
                      <p className="text-xs text-slate-400 mt-0.5">{fmtDay(t.published_at)}</p>
                    </div>
                    <span className="shrink-0 flex items-center gap-3 text-xs font-semibold text-slate-500">
                      <span className="flex items-center gap-1"><Heart className="h-3.5 w-3.5" />{t.reactions}</span>
                      <span className="flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" />{t.comments}</span>
                      <ExternalLink className="h-3.5 w-3.5 text-slate-300 group-hover:text-coral-dark" />
                    </span>
                  </a>
                ))}
              </div>
            </>
          ) : (
            <div data-testid="performance-empty" className="text-sm text-slate-500 space-y-3">
              {eng?.reason === "not_connected" && (
                <p>Connectez votre compte LinkedIn ci-dessus : Pulse affichera ici les réactions et les commentaires reçus sur vos publications.</p>
              )}
              {eng?.reason === "no_real_post" && (
                <p>Publiez votre premier post depuis Pulse : vous verrez ici les réactions et les commentaires qu'il récolte.</p>
              )}
              {(eng?.reason === "not_permitted" || eng?.reason === "error") && (
                <p>
                  LinkedIn ne transmet pas les statistiques de vos publications à Pulse pour le moment.
                  Vous les retrouvez dans « Impressions » sous chacun de vos posts sur LinkedIn.
                </p>
              )}
              {!eng && <p>Chargement…</p>}
            </div>
          )}

          {count("failed") > 0 && (
            <div className="flex items-center gap-2 text-destructive text-sm mt-4">
              <AlertTriangle className="h-4 w-4" /> {count("failed")} publication(s) en échec, voir « Mes posts ».
            </div>
          )}
        </div>
      </div>

      {/* Prochaines publications */}
      {stats?.upcoming?.length > 0 && (
        <div data-testid="upcoming-card" className="bg-white border border-slate-200 rounded-sm p-6 mt-6">
          <h3 className="font-serif text-2xl text-slate-900 mb-4">Ce qui part bientôt</h3>
          <div className="space-y-2">
            {stats.upcoming.map((u) => (
              <button key={u.id} onClick={() => navigate("/calendrier")} data-testid={`upcoming-${u.id}`}
                className="w-full flex items-start gap-3 p-3 text-left border border-slate-100 rounded-sm hover:border-coral transition-colors">
                <Clock className="h-4 w-4 text-coral-dark shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700 truncate">{u.excerpt}…</p>
                  <p className="text-xs text-slate-400 mt-0.5">{fmtDayTime(u.at)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Consommation IA : réservé à l'administration */}
      {isAdmin && (
        <div data-testid="usage-card" className="bg-white border border-slate-200 rounded-sm p-6 mt-6">
          <h3 className="font-serif text-2xl text-slate-900 mb-4">Détail de consommation <span className="text-xs font-sans font-semibold uppercase tracking-[0.12em] text-slate-400 align-middle ml-2">Admin</span></h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Tokens en entrée</span>
              <span className="font-semibold text-slate-900">{(usage?.input_tokens ?? 0).toLocaleString("fr-FR")}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Tokens en sortie</span>
              <span className="font-semibold text-slate-900">{(usage?.output_tokens ?? 0).toLocaleString("fr-FR")}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Illustrations IA ce mois-ci</span>
              <span className="font-semibold text-slate-900">{usage?.images_this_month ?? 0} / {usage?.image_limit ?? "…"}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-500">Coût total (texte + images)</span>
              <span className="font-semibold text-coral-dark">{((usage?.cost_eur ?? 0) + (usage?.image_cost_eur ?? 0)).toFixed(2)} €</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
